/**
 * Enhanced Email Validation Module
 *
 * This module provides comprehensive email validation with:
 * - Syntax validation
 * - DNS/MX lookup validation (via external API)
 * - Typo detection and suggestions
 * - Fail-open behavior for network errors
 * - Specific error messages
 */
import axios from "axios";
import Mailcheck from "mailcheck";
import validator from "validator";

import { API_EMAIL_VALIDATION } from "../../constants/api";

// ============================================================================
// Types
// ============================================================================

export type EmailValidationStatus =
  | "valid"
  | "invalid"
  | "unknown"
  | "pending"
  | "idle";

export type EmailErrorType =
  | "required"
  | "format"
  | "domain"
  | "advisor"
  | "network"
  | "disposable"
  | "role_based"
  | null;

export interface EmailValidationResult {
  isValid: boolean;
  status: EmailValidationStatus;
  errorType: EmailErrorType;
  errorMessage: string | null;
  suggestion: EmailSuggestion | null;
  wasNetworkError: boolean;
}

export interface EmailSuggestion {
  address: string;
  domain: string;
  full: string;
}

interface DNSValidationResponse {
  ok: boolean;
  message: string;
  data: any;
}

// ============================================================================
// Configuration
// ============================================================================

// Common domains to check against for typos
const COMMON_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.com.mx",
  "hotmail.com",
  "outlook.com",
  "outlook.es",
  "live.com",
  "live.com.mx",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "msn.com",
  "ymail.com",
  // Mexican providers
  "telmex.com",
  "prodigy.net.mx",
  "infinitum.com.mx",
];

const COMMON_SECOND_LEVEL_DOMAINS = [
  "yahoo",
  "hotmail",
  "gmail",
  "outlook",
  "live",
];

const COMMON_TOP_LEVEL_DOMAINS = [
  "com",
  "com.mx",
  "mx",
  "net",
  "org",
  "edu",
  "gov",
  "co",
  "io",
  "es",
];

// Known disposable email domains (comprehensive list)
const DISPOSABLE_DOMAINS = new Set([
  // Original list
  "tempmail.com",
  "guerrillamail.com",
  "guerrillamail.org",
  "10minutemail.com",
  "throwaway.email",
  "mailinator.com",
  "maildrop.cc",
  "temp-mail.org",
  "fakeinbox.com",
  "yopmail.com",
  "trashmail.com",
  "getnada.com",
  "mohmal.com",
  "sharklasers.com",

  // Extended list - Popular services
  "tempail.com",
  "tempr.email",
  "discard.email",
  "discardmail.com",
  "disposablemail.com",
  "dropmail.me",
  "emailondeck.com",
  "fakemailgenerator.com",
  "getairmail.com",
  "guerrillamail.biz",
  "guerrillamail.de",
  "guerrillamail.net",
  "harakirimail.com",
  "hmamail.com",
  "mailcatch.com",
  "mailchop.com",
  "mailexpire.com",
  "mailnesia.com",
  "mailsac.com",
  "mailtemp.net",
  "mintemail.com",
  "mytrashmail.com",
  "nada.email",
  "owlymail.com",
  "proxymail.eu",
  "rcpt.at",
  "spambox.us",
  "spamfree24.org",
  "spamgourmet.com",
  "spamherelots.com",
  "spamobox.com",
  "tempmailaddress.com",
  "tempmailer.com",
  "tempmailrapid.com",
  "temporaryemail.net",
  "temporaryinbox.com",
  "thankyou2010.com",
  "thisisnotmyrealemail.com",
  "throwawayemailaddress.com",
  "tmail.ws",
  "trash-mail.at",
  "trashmail.net",
  "wegwerfmail.de",
  "wegwerfmail.net",
  "yep.it",
  "zmail.info",

  // Mexican/Spanish focused
  "correotemporal.com",
  "emailtemporal.org",
  "tempinbox.com",
  "burnermail.io",
  "inboxkitten.com",
  "mailpoof.com",
  "gmailnator.com",
  "emailfake.com",
  "crazymailing.com",
  "tempemailgen.com",
]);

// Role-based email prefixes that suggest generic/shared emails
const ROLE_BASED_PREFIXES = new Set([
  "admin",
  "administrator",
  "info",
  "contact",
  "contacto",
  "support",
  "soporte",
  "sales",
  "ventas",
  "help",
  "ayuda",
  "service",
  "servicio",
  "billing",
  "facturacion",
  "hr",
  "rh",
  "jobs",
  "careers",
  "marketing",
  "press",
  "prensa",
  "media",
  "news",
  "noticias",
  "webmaster",
  "postmaster",
  "hostmaster",
  "noreply",
  "no-reply",
  "no_reply",
  "donotreply",
  "mailer-daemon",
  "abuse",
  "security",
  "seguridad",
  "legal",
  "compliance",
  "privacy",
  "privacidad",
  "feedback",
  "enquiries",
  "inquiries",
  "office",
  "oficina",
  "reception",
  "recepcion",
  "hello",
  "hola",
  "hi",
  "team",
  "equipo",
]);

// Error messages in Spanish
const ERROR_MESSAGES = {
  required: "El correo electrónico es requerido",
  format: "El formato del correo electrónico no es válido",
  domain: "El dominio del correo no existe o no puede recibir emails",
  advisor: "No puedes usar tu propio correo electrónico",
  network: "No se pudo verificar el correo. Intenta de nuevo.",
  disposable: "No se permiten correos temporales o desechables",
  role_based: "Se recomienda usar un correo personal en lugar de uno genérico",
};

// ============================================================================
// Core Validation Functions
// ============================================================================

/**
 * Validates email syntax using validator library
 */
export const validateEmailSyntax = (email: string): boolean => {
  if (!email || email.trim() === "") return false;
  return validator.isEmail(email.trim());
};

/**
 * Checks if the email domain is a known disposable email provider
 */
export const isDisposableEmail = (email: string): boolean => {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? DISPOSABLE_DOMAINS.has(domain) : false;
};

/**
 * Checks if the email uses a role-based prefix (e.g., admin@, info@)
 * These are typically shared mailboxes, not personal emails
 */
export const isRoleBasedEmail = (email: string): boolean => {
  const localPart = email.split("@")[0]?.toLowerCase();
  if (!localPart) return false;

  // Direct match
  if (ROLE_BASED_PREFIXES.has(localPart)) return true;

  // Check for common patterns like "info.company" or "admin_mx"
  for (const prefix of ROLE_BASED_PREFIXES) {
    if (
      localPart.startsWith(`${prefix}.`) ||
      localPart.startsWith(`${prefix}_`) ||
      localPart.startsWith(`${prefix}-`)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Checks for typos and returns suggestion if found
 */
export const checkEmailTypo = (
  email: string,
): Promise<EmailSuggestion | null> => {
  return new Promise((resolve) => {
    Mailcheck.run({
      email: email.trim(),
      domains: COMMON_DOMAINS,
      secondLevelDomains: COMMON_SECOND_LEVEL_DOMAINS,
      topLevelDomains: COMMON_TOP_LEVEL_DOMAINS,
      suggested: (suggestion: EmailSuggestion) => {
        resolve(suggestion);
      },
      empty: () => {
        resolve(null);
      },
    });
  });
};

/**
 * Validates email domain via DNS lookup (external API)
 * Uses FAIL-OPEN behavior for network errors
 */
export const validateEmailDNS = async (
  email: string,
): Promise<{
  isValid: boolean;
  wasNetworkError: boolean;
  message?: string;
}> => {
  // Skip DNS validation when explicitly disabled via env variable
  if (import.meta.env.VITE_SKIP_EMAIL_DNS_VALIDATION === "true") {
    return { isValid: true, wasNetworkError: false };
  }

  const url = `${API_EMAIL_VALIDATION}/dns/lookup`;

  try {
    const resp = await axios.post<DNSValidationResponse>(
      url,
      { email: email.trim() },
      { timeout: 8000 }, // Slightly shorter timeout for better UX
    );

    if (resp.status === 200 && resp.data.ok) {
      return { isValid: true, wasNetworkError: false };
    }

    return {
      isValid: false,
      wasNetworkError: false,
      message: resp.data.message || ERROR_MESSAGES.domain,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Handle 4xx responses as validation failures (not network errors)
      // Backend returns 400 with {ok: false, message: "..."} for invalid domains
      // Always use Spanish error message regardless of backend response
      if (
        error.response?.status &&
        error.response.status >= 400 &&
        error.response.status < 500
      ) {
        return {
          isValid: false,
          wasNetworkError: false,
          message: ERROR_MESSAGES.domain,
        };
      }

      // FAIL-OPEN: True network errors (5xx, timeout, no response) should not block users
      console.warn("Email DNS validation unavailable:", {
        status: error.response?.status,
        message: error.message,
        code: error.code,
      });
    }

    // Return valid=true but flag that it was a network error
    // Backend will do final validation
    return { isValid: true, wasNetworkError: true };
  }
};

/**
 * Validates that the email is not the current user's email
 */
export const validateNotAdvisorEmail = (
  email: string,
  userData: { email?: string; optional_email?: string } | null,
): boolean => {
  if (!userData || !email) return true;

  const trimmedEmail = email.trim().toLowerCase();
  const advisorPrimaryEmail = userData.email?.toLowerCase();
  const advisorSecondaryEmail = userData.optional_email?.toLowerCase();

  return (
    trimmedEmail !== advisorPrimaryEmail &&
    trimmedEmail !== advisorSecondaryEmail
  );
};

// ============================================================================
// Main Validation Function
// ============================================================================

export interface FullValidationOptions {
  /** Whether the email is required */
  required?: boolean;
  /** Check DNS/MX records */
  checkDNS?: boolean;
  /** Check for disposable emails */
  checkDisposable?: boolean;
  /** Check for role-based emails (warning, not blocking) */
  checkRoleBased?: boolean;
  /** User data to check against (for "not advisor" validation) */
  userData?: { email?: string; optional_email?: string } | null;
  /** Custom error messages */
  customMessages?: Partial<typeof ERROR_MESSAGES>;
}

/**
 * Performs full email validation with all checks
 * Returns detailed result with status, errors, and suggestions
 */
export const validateEmailFull = async (
  email: string,
  options: FullValidationOptions = {},
): Promise<EmailValidationResult> => {
  const {
    required = false,
    checkDNS = true,
    checkDisposable = true,
    checkRoleBased = true,
    userData = null,
    customMessages = {},
  } = options;

  const messages = { ...ERROR_MESSAGES, ...customMessages };
  const trimmedEmail = email?.trim() || "";

  // Initial result
  const result: EmailValidationResult = {
    isValid: true,
    status: "valid",
    errorType: null,
    errorMessage: null,
    suggestion: null,
    wasNetworkError: false,
  };

  // 1. Required check
  if (!trimmedEmail) {
    if (required) {
      return {
        ...result,
        isValid: false,
        status: "invalid",
        errorType: "required",
        errorMessage: messages.required,
      };
    }
    // Empty but not required = valid
    return { ...result, status: "idle" };
  }

  // 2. Syntax validation
  if (!validateEmailSyntax(trimmedEmail)) {
    return {
      ...result,
      isValid: false,
      status: "invalid",
      errorType: "format",
      errorMessage: messages.format,
    };
  }

  // 3. Check for typos (non-blocking, just add suggestion)
  const suggestion = await checkEmailTypo(trimmedEmail);
  if (suggestion) {
    result.suggestion = suggestion;
  }

  // 4. Disposable email check
  if (checkDisposable && isDisposableEmail(trimmedEmail)) {
    return {
      ...result,
      isValid: false,
      status: "invalid",
      errorType: "disposable",
      errorMessage: messages.disposable,
    };
  }

  // 5. Advisor email check
  if (userData && !validateNotAdvisorEmail(trimmedEmail, userData)) {
    return {
      ...result,
      isValid: false,
      status: "invalid",
      errorType: "advisor",
      errorMessage: messages.advisor,
    };
  }

  // 5.5. Role-based email check (warning, not blocking)
  if (checkRoleBased && isRoleBasedEmail(trimmedEmail)) {
    return {
      ...result,
      isValid: true, // Allow but warn
      status: "unknown",
      errorType: "role_based",
      errorMessage: messages.role_based,
      suggestion: null, // Clear any typo suggestion to avoid confusion
      wasNetworkError: false,
    };
  }

  // 6. DNS validation (async, with fail-open)
  if (checkDNS) {
    const dnsResult = await validateEmailDNS(trimmedEmail);
    result.wasNetworkError = dnsResult.wasNetworkError;

    if (!dnsResult.isValid) {
      return {
        ...result,
        isValid: false,
        status: "invalid",
        errorType: "domain",
        errorMessage: dnsResult.message || messages.domain,
      };
    }

    // If there was a network error but we're allowing it through
    if (dnsResult.wasNetworkError) {
      result.status = "unknown";
    }
  }

  return result;
};

